import {XAxisOrientation} from 'ui-library/charting/internals/Axis';
import {Margin} from 'ui-library/charting/internals/VisxUtilityTypes';

import getChartDimensions from './getChartDimensions';
import {VisxAxisConfig} from './getVisxAxisConfig';
import getXTickLabelRotationAndMargins from './getXTickLabelRotationAndMargins';
import getYTickLabelMargins from './getYTickLabelMargins';

interface FitChartAndLabelsInput<TXVisxAxisConfig, TYVisxAxisConfig> {
  defaultMargins: Margin;
  width: number;
  height: number;
  xAxisOrientation: XAxisOrientation;
  xAxisConfig: TXVisxAxisConfig;
  yAxisConfig: TYVisxAxisConfig;
  extraMargin?: Margin;
}

interface FitChartAndLabelsOutput<TXVisxAxisConfig, TYVisxAxisConfig> {
  chartWidth: number;
  chartHeight: number;
  margins: Margin;
  xAxisConfig: TXVisxAxisConfig;
  yAxisConfig: TYVisxAxisConfig;
}

function addMargins(a: Margin, b: Margin): Margin {
  return {
    top: a.top + b.top,
    left: a.left + b.left,
    right: a.right + b.right,
    bottom: a.bottom + b.bottom,
  };
}

/**
 * Adjust margins and x-axis tick label rotations based on the chart width and x/y axis configs to fit all x/y
 * axis tick labels on the chart.
 */
export default function fitChartAndLabels<
  TXVisxAxisConfig extends VisxAxisConfig,
  TYVisxAxisConfig extends VisxAxisConfig,
>({
  defaultMargins,
  width,
  height,
  xAxisOrientation,
  xAxisConfig,
  yAxisConfig,
  extraMargin,
}: FitChartAndLabelsInput<TXVisxAxisConfig, TYVisxAxisConfig>): FitChartAndLabelsOutput<
  TXVisxAxisConfig,
  TYVisxAxisConfig
> {
  const {margins: marginsFromXTicks, rotation: xTickLabelRotation} = getXTickLabelRotationAndMargins({
    currentMargins: defaultMargins,
    totalWidth: width,
    xAxisConfig,
    xAxisOrientation,
  });
  const margins = getYTickLabelMargins({
    currentMargins: marginsFromXTicks,
    yAxisConfig,
  });

  const {chartWidth, chartHeight} = getChartDimensions(margins, width, height);

  return {
    chartWidth,
    chartHeight,
    margins: extraMargin ? addMargins(margins, extraMargin) : margins,
    xAxisConfig: {
      ...xAxisConfig,
      scale: xAxisConfig.scale.copy().range([0, chartWidth]),
      tickLabelRotation: xTickLabelRotation,
    },
    yAxisConfig: {
      ...yAxisConfig,
      scale: yAxisConfig.scale.copy().range([0, chartHeight]),
    },
  };
}
